import fr from './locales/fr.json';
import en from './locales/en.json';
import type { LanguageCode } from './index';

export interface LocalisedQuestion {
  prompt: string;
  options: Record<string, string>;
}

/**
 * The question bank, indexed by stable id rather than array position.
 *
 * Deliberately read straight from the JSON instead of through i18next: the
 * server identifies a question by `id`, and i18next can only address an array
 * by position. Going through `t('questions.7.prompt')` would silently return
 * the wrong question the moment anything is inserted or removed above it —
 * exactly the bug stable ids exist to prevent.
 *
 * The two languages may therefore also be ordered differently; only the ids
 * have to line up, which `check-locales` enforces.
 */
/** Shape of a parsed locale file, as far as this module cares. */
interface LocaleDoc {
  questions: { id: number; prompt: string; options: Record<string, string> }[];
}

function index(doc: unknown) {
  // The JSON import infers a distinct literal type per question, since each has
  // its own set of option keys — unhelpfully narrow here. `check-locales` is
  // what actually guarantees the shape.
  const { questions } = doc as LocaleDoc;
  return new Map<number, LocalisedQuestion>(
    questions.map((q) => [q.id, { prompt: q.prompt, options: q.options }]),
  );
}

const BANKS: Record<LanguageCode, Map<number, LocalisedQuestion>> = {
  fr: index(fr),
  en: index(en),
};

/** Look a question up in `language`, falling back to French. */
export function findQuestion(id: number, language: string): LocalisedQuestion | undefined {
  const bank = BANKS[language as LanguageCode] ?? BANKS.fr;
  return bank.get(id) ?? BANKS.fr.get(id);
}
